import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { trans } from "sharedKernel/i18n";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { EvaluatorInviteRow } from "./contracts";
import { HubTable } from "components/HubDefaultTable";
import { COMPANY_EVALUATION_INVITE_STATUS } from "sharedKernel/constants";
import { CenterContent } from "../../../solutions/table/style";
import { AnimatedSortButtons } from "components/HubDefaultTable/OrderByButton";
import { sortRows } from "./behavior";

export interface HubCompanyEvaluatorsInviteTableProps {
  header: string[];
  rows: EvaluatorInviteRow[];
  onNotifyAgain?: (evaluator: EvaluatorInviteRow) => void;
  onRemove?: (evaluator: EvaluatorInviteRow) => void;
}

export interface SortTable {
  statusAsc: boolean;
  nameAsc: boolean;
}
export interface OrderClick {
  statusClick: boolean;
  nameClick: boolean;
}
export const HubCompanyEvaluatorsInviteTable = ({
  header,
  rows,
  onNotifyAgain,
  onRemove,
}: HubCompanyEvaluatorsInviteTableProps): JSX.Element => {
  const [sortTable, setSortTable] = useState<SortTable>({
    statusAsc: false,
    nameAsc: false,
  });
  const [orderClick, setOrderClick] = useState<OrderClick>({
    statusClick: false,
    nameClick: false,
  });
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  const status = {};
  status[COMPANY_EVALUATION_INVITE_STATUS.ACCEPTED] = {
    color: "green",
    label: trans("Cadastrado"),
  };
  status[COMPANY_EVALUATION_INVITE_STATUS.INVITED] = {
    color: "orange",
    label: trans("Não Cadastrado"),
  };
  status[COMPANY_EVALUATION_INVITE_STATUS.ERROR] = {
    color: "red",
    label: trans("Erro"),
  };
  status[COMPANY_EVALUATION_INVITE_STATUS.REFUSED] = {
    color: "red",
    label: trans("Recusado"),
  };
  status[COMPANY_EVALUATION_INVITE_STATUS.EXPIRED] = {
    color: "gray",
    label: trans("Expirado"),
  };
  status[COMPANY_EVALUATION_INVITE_STATUS.CANCELED] = {
    color: "gray",
    label: trans("Cancelado"),
  };
  const labels = {
    evaluator: trans("Avaliador"),
    status: trans("Status"),
    actions: trans("Ações"),
    notifyAgain: trans("Notificar novamente"),
    removeEvaluator: trans("Remover avaliador"),
  };

  const tableHeader = (): any[] => {
    return [
      getCenteredContent(buildHeaderName()),
      getCenteredContent(buildHeaderStatus()),
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          paddingRight: "16px",
        }}
      >
        {labels.actions}
      </div>,
    ];
  };

  const buildHeaderName = (): JSX.Element => {
    return (
      <>
        {labels.evaluator}
        <div className="ml-1">
          <AnimatedSortButtons
            orderByAsc={sortTable.nameAsc}
            onClick={() => {
              setSortTable({ ...sortTable, nameAsc: !sortTable.nameAsc });
              setOrderClick({
                ...orderClick,
                nameClick: true,
                statusClick: false,
              });
            }}
          />
        </div>
      </>
    );
  };

  const buildHeaderStatus = (): JSX.Element => {
    return (
      <>
        {labels.status}
        <div className="ml-1">
          <AnimatedSortButtons
            orderByAsc={sortTable.statusAsc}
            onClick={() => {
              setSortTable({ ...sortTable, statusAsc: !sortTable.statusAsc });
              setOrderClick({
                ...orderClick,
                statusClick: true,
                nameClick: false,
              });
            }}
          />
        </div>
      </>
    );
  };

  const getCenteredContent = (content: any): any => {
    return <CenterContent>{content}</CenterContent>;
  };

  const getStatusLabel = (statusCode: string): string => {
    return status[statusCode]?.label ?? statusCode;
  };

  const buildActionsColumn = (evaluator: EvaluatorInviteRow): JSX.Element => {
    const popoverId = evaluator.id || evaluator.email || evaluator.name;
    const canNotify =
      Boolean(evaluator.id) &&
      (evaluator.status === COMPANY_EVALUATION_INVITE_STATUS.INVITED ||
        evaluator.status === COMPANY_EVALUATION_INVITE_STATUS.ACCEPTED);
    const canRemove =
      Boolean(evaluator.id) &&
      evaluator.status !== "evaluating";

    const popoverContent = (
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "4px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          padding: "8px 0",
          minWidth: "200px",
          color: "#333",
          margin: "-10px",
        }}
      >
        {canNotify && (
          <div
            onClick={() => {
              setOpenPopoverId(null);
              onNotifyAgain?.(evaluator);
            }}
            style={{
              padding: "10px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontSize: "14px",
              color: "#333",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#f5f5f5")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <FontAwesomeIcon
              icon={["fas", "envelope"]}
              style={{ width: "16px", color: "#333" }}
            />
            <span>{labels.notifyAgain}</span>
          </div>
        )}
        {canRemove && (
          <div
            onClick={() => {
              setOpenPopoverId(null);
              onRemove?.(evaluator);
            }}
            style={{
              padding: "10px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontSize: "14px",
              color: "#333",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#f5f5f5")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <FontAwesomeIcon
              icon={["fas", "trash"]}
              style={{ width: "16px", color: "#333" }}
            />
            <span>{labels.removeEvaluator}</span>
          </div>
        )}
      </div>
    );

    if (!canNotify && !canRemove) {
      return <div />;
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          paddingRight: "16px",
        }}
      >
        <Tippy
          content={popoverContent}
          visible={openPopoverId === popoverId}
          onClickOutside={() => setOpenPopoverId(null)}
          interactive={true}
          placement="bottom-end"
          arrow={false}
          offset={[0, 4]}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              setOpenPopoverId(
                openPopoverId === popoverId ? null : popoverId
              );
            }}
            style={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "6px 10px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon
              icon={["fas", "ellipsis-vertical"]}
              style={{ fontSize: "16px" }}
            />
          </div>
        </Tippy>
      </div>
    );
  };

  const getOrderedRows = (): EvaluatorInviteRow[] => {
    let newRows = sortRows(rows, sortTable.nameAsc, orderClick.nameClick);

    if (orderClick.statusClick) {
      const acceptedEvaluators = newRows.filter(
        (row) => row.status === COMPANY_EVALUATION_INVITE_STATUS.ACCEPTED
      );
      const invitedEvaluators = newRows.filter(
        (row) => row.status === COMPANY_EVALUATION_INVITE_STATUS.INVITED
      );
      const errorEvaluators = newRows.filter(
        (row) => row.status === COMPANY_EVALUATION_INVITE_STATUS.ERROR
      );
      const refusedEvaluators = newRows.filter(
        (row) => row.status === COMPANY_EVALUATION_INVITE_STATUS.REFUSED
      );
      const expiredEvaluators = newRows.filter(
        (row) => row.status === COMPANY_EVALUATION_INVITE_STATUS.EXPIRED
      );
      const canceledEvaluators = newRows.filter(
        (row) => row.status === COMPANY_EVALUATION_INVITE_STATUS.CANCELED
      );
      if (sortTable.statusAsc) {
        newRows = [
          ...canceledEvaluators,
          ...expiredEvaluators,
          ...refusedEvaluators,
          ...errorEvaluators,
          ...invitedEvaluators,
          ...acceptedEvaluators,
        ];
      } else {
        newRows = [
          ...acceptedEvaluators,
          ...invitedEvaluators,
          ...errorEvaluators,
          ...refusedEvaluators,
          ...expiredEvaluators,
          ...canceledEvaluators,
        ];
      }
    }
    return newRows;
  };

  const getRows = (): JSX.Element[][] => {
    return getOrderedRows().map((evaluator) => [
      <>{evaluator.name}</>,
      <CenterContent>{getStatusLabel(evaluator.status)}</CenterContent>,
      buildActionsColumn(evaluator),
    ]);
  };

  const headerSize = ["33%", "42%", "25%"];
  return (
    <HubTable header={tableHeader()} rows={getRows()} headerSize={headerSize} />
  );
};
